/**
 * Conversations hooks — Module 10 (partner-side visibility + co-pilot).
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@/lib/api';

export interface ConversationRow {
  id: string;
  prospect_id: string;
  prospect_display_name: string | null;
  prospect_handle: string | null;
  channel: string;
  status: string;
  warmth_score: number;
  detected_intents: string[];
  partner_unread_count: number;
  rdv_proposals_count: number;
  rdv_booked_at: string | null;
  copilot_mode_active: boolean;
  inea_paused_by_partner: boolean;
  last_inbound_at: string | null;
  last_outbound_at: string | null;
  updated_at: string;
  /** Lot 3 — conversations réseau (Instagram, Facebook…) via l'agrégateur. */
  participant_name?: string | null;
  participant_username?: string | null;
  participant_avatar?: string | null;
  pending_suggestion?: string | null;
  pending_suggestion_at?: string | null;
  /** Renseignés par GET /conversations/:id uniquement. */
  window_open_until?: string | null;
  window_open?: boolean;
}

export interface TranscriptItem {
  id: string;
  direction: 'inbound' | 'outbound' | 'system';
  kind: string;
  content: string | null;
  meta: Record<string, unknown>;
  occurred_at: string;
}

export interface ConversationDetail {
  conversation: ConversationRow;
  transcript: TranscriptItem[];
}

export interface ConversationListFilters {
  status?: string;
  unread?: boolean;
  prospectId?: string;
}

export function useConversations(filters: ConversationListFilters = {}) {
  const params = new URLSearchParams();
  if (filters.status) params.set('status', filters.status);
  if (filters.unread) params.set('unread', '1');
  if (filters.prospectId) params.set('prospect_id', filters.prospectId);
  const qs = params.toString();
  return useQuery<ConversationRow[]>({
    queryKey: ['conversations', filters],
    queryFn: () => apiRequest<ConversationRow[]>(`/conversations${qs ? `?${qs}` : ''}`),
    staleTime: 15_000,
    refetchInterval: 30_000,
  });
}

export function useConversation(id: string | null) {
  return useQuery<ConversationDetail>({
    queryKey: ['conversation', id],
    queryFn: () => apiRequest<ConversationDetail>(`/conversations/${id}`),
    enabled: Boolean(id),
    refetchInterval: id ? 10_000 : false,
  });
}

export function useMarkRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiRequest<void>(`/conversations/${id}/mark-read`, { method: 'POST' }),
    onSuccess: (_d, id) => {
      qc.invalidateQueries({ queryKey: ['conversations'] });
      qc.invalidateQueries({ queryKey: ['conversation', id] });
    },
  });
}

export function useConversationAction(action: 'pause' | 'resume' | 'copilot-toggle') {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiRequest<unknown>(`/conversations/${id}/${action}`, { method: 'POST' }),
    onSuccess: (_d, id) => {
      qc.invalidateQueries({ queryKey: ['conversations'] });
      qc.invalidateQueries({ queryKey: ['conversation', id] });
    },
  });
}

export function useCloseConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: 'cold' | 'rdv' | 'stop' }) =>
      apiRequest<void>(`/conversations/${id}/close`, {
        method: 'POST',
        body: { reason },
      }),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['conversations'] });
      qc.invalidateQueries({ queryKey: ['conversation', vars.id] });
    },
  });
}

export interface CopilotSendResult {
  ok: boolean;
  text: string;
}

export function useCopilotSend() {
  const qc = useQueryClient();
  return useMutation<CopilotSendResult, Error, { id: string; draft: string; mode: 'raw' | 'reformulate' }>({
    mutationFn: ({ id, draft, mode }) =>
      apiRequest<CopilotSendResult>(`/conversations/${id}/copilot-send`, {
        method: 'POST',
        body: { draft, mode },
      }),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['conversation', vars.id] });
      qc.invalidateQueries({ queryKey: ['conversations'] });
    },
  });
}

export interface EngagementLink {
  id: string;
  deep_link: string;
  status: string;
  expires_at: string;
  created_at: string;
  consumed_at: string | null;
  click_count: number;
}

export function useProspectEngagementLinks(prospectId: string | null) {
  return useQuery<EngagementLink[]>({
    queryKey: ['engagement-links', prospectId],
    queryFn: () => apiRequest<EngagementLink[]>(`/prospect/${prospectId}/engagement-links`),
    enabled: Boolean(prospectId),
  });
}

export function useCreateEngagementLink() {
  const qc = useQueryClient();
  return useMutation<
    EngagementLink,
    Error,
    { prospectId: string; customGreeting?: string; customOfferHint?: string }
  >({
    mutationFn: ({ prospectId, customGreeting, customOfferHint }) =>
      apiRequest<EngagementLink>(`/prospect/${prospectId}/engagement-link`, {
        method: 'POST',
        body: {
          custom_greeting: customGreeting,
          custom_offer_hint: customOfferHint,
        },
      }),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['engagement-links', vars.prospectId] });
    },
  });
}

export function useRevokeEngagementLink() {
  const qc = useQueryClient();
  return useMutation<void, Error, { linkId: string; prospectId: string }>({
    mutationFn: ({ linkId }) =>
      apiRequest<void>(`/engagement-link/${linkId}`, { method: 'DELETE' }),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['engagement-links', vars.prospectId] });
    },
  });
}

/** Lot 3 — régénérer la suggestion d'Inéa (budget IA du membre). */
export function useSuggest() {
  const qc = useQueryClient();
  return useMutation<{ suggestion: string }, Error, string>({
    mutationFn: (id) => apiRequest<{ suggestion: string }>(`/conversations/${id}/suggest`, { method: 'POST' }),
    onSuccess: (_d, id) => {
      qc.invalidateQueries({ queryKey: ['conversation', id] });
    },
  });
}

/** Lot 3 — réponse privée à l'auteur d'un commentaire (depuis la timeline du prospect). */
export function useCommentReply() {
  return useMutation<{ ok: true }, Error, { interactionId: string; text: string }>({
    mutationFn: ({ interactionId, text }) =>
      apiRequest<{ ok: true }>('/conversations/comment-reply', { method: 'POST', body: { interaction_id: interactionId, text } }),
  });
}
